import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit"
import { GetOrderShipmentRes_OrderShipmentProduct as OrderShipmentProductRes } from "gen/api/order/service_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import { LedgerEntry_UomSnapshot as UomSnapshot } from "gen/ledger/models_pb"
import { FractionalMoney as FractionalMoneyPB } from "gen/proto/money/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  CostAdjustment as CostAdjustment,
  OrderShipment_Product as OrderShipmentProduct,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney, Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import {
  convertUoMQuantityToQuantity,
  safeConvertQuantityToUoMQuantity,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"

import { AppState } from "app/store"

import { calculateDistributedCostsByProductId } from "./CostAdjustments/distributeCostAdjustment"

export interface OrderShipmentState {
  receiveNotes: string
  costAdjustments: CostAdjustment[]
  invoiceDate: TimestampPB | null
  dueDate: TimestampPB | null
  invoiceNumber: string
  referenceNumber: string
  shipmentProductsById: Record<string, OrderShipmentProductRes>
}

export interface OrderShipmentFormState {
  original: OrderShipmentState
  current: OrderShipmentState
  isInitialized: boolean
}

export function emptyState(): OrderShipmentState {
  return {
    receiveNotes: "",
    costAdjustments: [],
    invoiceDate: null,
    dueDate: null,
    invoiceNumber: "",
    referenceNumber: "",
    shipmentProductsById: {},
  }
}

export function emptyFormState(): OrderShipmentFormState {
  return {
    original: emptyState(),
    current: emptyState(),
    isInitialized: false,
  }
}

export const orderShipmentsSlice = createSlice({
  name: "orderShipments",
  initialState: emptyFormState(),
  reducers: {
    resetForm: (state) => {
      state.current = emptyState()
      state.original = emptyState()
      state.isInitialized = false
    },
    setInitialState: (
      state,
      { payload: initialState }: PayloadAction<OrderShipmentState>
    ) => {
      state.original = initialState
      state.current = initialState
      state.isInitialized = true
    },
    setReceiveNotes: (
      { current },
      { payload: receiveNotes }: PayloadAction<string>
    ) => {
      current.receiveNotes = receiveNotes
    },
    setProductCost: (
      { current },
      {
        payload: { orderShipmentProductId, cost },
      }: PayloadAction<{
        orderShipmentProductId: UUID
        cost: FractionalMoney
      }>
    ) => {
      const existing =
        current.shipmentProductsById[orderShipmentProductId.toString()]?.product
      if (!existing) {
        return
      }
      existing.costV2 = cost
    },
    setCostAdjustments: (
      { current },
      { payload: costAdjustments }: PayloadAction<CostAdjustment[]>
    ) => {
      current.costAdjustments = costAdjustments
    },
    setInvoiceDate: (
      { current },
      { payload: invoiceDate }: PayloadAction<TimestampPB | null>
    ) => {
      current.invoiceDate = invoiceDate
    },
    setDueDate: (
      { current },
      { payload: dueDate }: PayloadAction<TimestampPB | null>
    ) => {
      current.dueDate = dueDate
    },
    setInvoiceNumber: (
      { current },
      { payload: invoiceNumber }: PayloadAction<string>
    ) => {
      current.invoiceNumber = invoiceNumber
    },
    setReferenceNumber: (
      { current },
      { payload: referenceNumber }: PayloadAction<string>
    ) => {
      current.referenceNumber = referenceNumber
    },
    setReceivedQuantity: (
      { current },
      {
        payload: { orderShipmentProductId, quantity },
      }: PayloadAction<{ orderShipmentProductId: PBUUID; quantity: Decimal }>
    ) => {
      const existing =
        current.shipmentProductsById[UUID.idString(orderShipmentProductId)]
          ?.product
      if (!existing) {
        return
      }
      const factor = uomConversionFactor(existing)
      existing.quantityReceivedV2 = convertUoMQuantityToQuantity(
        quantity,
        factor
      )
      existing.quantityReceivedUom = factor && quantity
    },
    setReceivedQuantityUom: (
      { current },
      {
        payload: { orderShipmentProductId, uomId, uomSnapshot },
      }: PayloadAction<{
        orderShipmentProductId: PBUUID
        uomId?: PBUUID
        uomSnapshot?: UomSnapshot
      }>
    ) => {
      const existing =
        current.shipmentProductsById[UUID.idString(orderShipmentProductId)]
          ?.product
      if (!existing) {
        return
      }
      const displayed = displayedQuantity(existing)
      const newFactor =
        uomId && uomSnapshot?.conversionFactorToBase
          ? Decimal.fromPB(uomSnapshot.conversionFactorToBase)
          : undefined
      existing.quantityReceivedV2 = convertUoMQuantityToQuantity(
        displayed,
        newFactor
      )
      existing.uomId = newFactor && uomId
      existing.uomSnapshot = newFactor && uomSnapshot
      existing.quantityReceivedUom = newFactor && displayed
    },
    addShipmentProduct: (
      { current },
      {
        payload: { product, cost, quantity, uomId, uomSnapshot },
      }: PayloadAction<{
        product: OrderShipmentProductRes
        cost: FractionalMoneyPB
        quantity: Decimal
        uomId?: PBUUID
        uomSnapshot?: UomSnapshot
      }>
    ) => {
      const shipmentProduct = product.product
      if (!shipmentProduct?.id) {
        return
      }
      const productIdStr = UUID.idString(shipmentProduct.productId)
      const existing = Object.values(current.shipmentProductsById).find(
        (p) =>
          p.product?.productId &&
          UUID.idString(p.product.productId) === productIdStr &&
          UUID.eqFromPB(p.product.uomId, uomId)
      )?.product
      if (existing) {
        const factor = uomConversionFactor(existing)
        const total = displayedQuantity(existing).add(quantity)
        existing.quantityReceivedV2 = convertUoMQuantityToQuantity(
          total,
          factor
        )
        existing.quantityReceivedUom = factor && total
        return
      }
      const idStr = UUID.idString(shipmentProduct.id)
      const factor =
        uomId && uomSnapshot?.conversionFactorToBase
          ? Decimal.fromPB(uomSnapshot.conversionFactorToBase)
          : undefined
      current.shipmentProductsById[idStr] = {
        ...product,
        product: {
          ...shipmentProduct,
          costV2: cost,
          quantityReceivedV2: convertUoMQuantityToQuantity(quantity, factor),
          uomId: factor && uomId,
          uomSnapshot: factor && uomSnapshot,
          quantityReceivedUom: factor && quantity,
        },
      }
    },
    removeShipmentProduct: (
      { current },
      { payload: orderShipmentProductId }: PayloadAction<PBUUID>
    ) => {
      delete current.shipmentProductsById[UUID.idString(orderShipmentProductId)]
    },
  },
})

function uomConversionFactor(q: OrderShipmentProduct): Decimal | undefined {
  if (!q.uomId || !q.uomSnapshot?.conversionFactorToBase) {
    return undefined
  }
  const factor = Decimal.fromPB(q.uomSnapshot.conversionFactorToBase)
  return factor.gt(Decimal.zero()) ? factor : undefined
}

export function displayedQuantity(q: OrderShipmentProduct): Decimal {
  const factor = uomConversionFactor(q)
  if (!factor) {
    return Decimal.fromPBOrZero(q.quantityReceivedV2)
  }
  return safeConvertQuantityToUoMQuantity(
    Decimal.fromPBOrZero(q.quantityReceivedV2),
    factor
  )
}

export const {
  setInitialState,
  setReceiveNotes,
  setProductCost,
  setCostAdjustments,
  setInvoiceDate,
  setDueDate,
  setInvoiceNumber,
  setReferenceNumber,
  setReceivedQuantity,
  setReceivedQuantityUom,
  addShipmentProduct,
  removeShipmentProduct,
  resetForm,
} = orderShipmentsSlice.actions

export const selectIsInitialized = (state: AppState) =>
  state.orderShipments.isInitialized

export function selectIsEdited({
  orderShipments: { original, current },
}: Pick<AppState, "orderShipments">) {
  return [
    current.receiveNotes === original.receiveNotes,
    TimestampPB.equals(
      current.dueDate ?? undefined,
      original.dueDate ?? undefined
    ),
    TimestampPB.equals(
      current.invoiceDate ?? undefined,
      original.invoiceDate ?? undefined
    ),
    current.invoiceNumber === original.invoiceNumber,
    current.referenceNumber === original.referenceNumber,
    isCostAdjustmentsEqual(current.costAdjustments, original.costAdjustments),
    isShipmentProductsEqual(
      current.shipmentProductsById,
      original.shipmentProductsById
    ),
  ].some((isEdited) => !isEdited)
}

export function selectShipmentProductsById(state: AppState) {
  return state.orderShipments.current.shipmentProductsById
}

export const selectReceiveNotes = (state: AppState) =>
  state.orderShipments.current.receiveNotes

export function selectCostAdjustments({
  orderShipments: { current },
}: AppState) {
  return current.costAdjustments
}

export const selectDistributedCostsByProductId = createSelector(
  selectCostAdjustments,
  selectShipmentProductsById,
  (costAdjustments, shipmentProductsById) => {
    const products = Object.values(shipmentProductsById)
      .map((shipmentProduct) => shipmentProduct.product)
      .filter((product): product is OrderShipmentProduct => !!product)

    const costsById: Record<string, FractionalMoneyPB> = {}
    for (const product of products) {
      if (product.id && product.costV2) {
        costsById[UUID.idString(product.id)] = product.costV2
      }
    }

    return calculateDistributedCostsByProductId(
      costAdjustments,
      products,
      costsById
    )
  }
)

export function selectInvoiceDate(state: AppState) {
  return state.orderShipments.current.invoiceDate
}

export function selectDueDate(state: AppState) {
  return state.orderShipments.current.dueDate
}

export function selectInvoiceNumber(state: AppState) {
  return state.orderShipments.current.invoiceNumber
}

export function selectReferenceNumber(state: AppState) {
  return state.orderShipments.current.referenceNumber
}

function isShipmentProductsEqual(
  a: Record<string, OrderShipmentProductRes>,
  b: Record<string, OrderShipmentProductRes>
) {
  const aIds = Object.keys(a)
  if (aIds.length !== Object.keys(b).length) return false
  return aIds.every((id) => {
    const bp = b[id]?.product
    return (
      bp &&
      Decimal.fromPBOrZero(a[id].product?.quantityReceivedV2).eq(
        Decimal.fromPBOrZero(bp.quantityReceivedV2)
      ) &&
      FractionalMoney.fromPB(a[id].product?.costV2).eq(
        FractionalMoney.fromPB(bp.costV2)
      ) &&
      UUID.eqFromPB(a[id].product?.uomId, bp.uomId)
    )
  })
}

function isCostAdjustmentsEqual(a: CostAdjustment[], b: CostAdjustment[]) {
  return (
    a.length === b.length &&
    a.every((_, i) => isCostAdjustmentEqual(a[i], b[i]))
  )
}

function isCostAdjustmentEqual(a: CostAdjustment, b: CostAdjustment) {
  if (a.name !== b.name) {
    return false
  }

  if (!Money.fromPB(a.amount).eq(Money.fromPB(b.amount))) {
    return false
  }

  if (!UUID.eqFromPB(a.chartOfAccountId, b.chartOfAccountId)) {
    return false
  }

  if (a.distributionMethod !== b.distributionMethod) {
    return false
  }

  return true
}
